/**
 * End-to-end encryption for the team chat. The server side is classes/Chat.php; the
 * store side is chat.js (the e2ee* actions). Nothing here talks to the network.
 *
 * Every person: an ECDH P-256 key pair made here. The public key is published; the
 * private key lives in this browser's IndexedDB (non-extractable) and, if the person
 * chose a passphrase, as a PBKDF2 + AES-GCM encrypted backup the server stores.
 * Every channel: an AES-GCM-256 key. Whoever writes first makes it and wraps it for
 * each member (ECIES: ephemeral ECDH + HKDF + AES-GCM), one row per member.
 * A message: "e1.<key version>.<iv>.<ciphertext>", all base64.
 */
const DB_NAME = "connect-chat";
const STORE = "kv";
const CURVE = { name: "ECDH", namedCurve: "P-256" };
const WRAP_INFO = "connect-chat-wrap-v1";
const PBKDF2_ITER = 300000;

export const supported = () => !!(window.crypto && window.crypto.subtle && window.indexedDB && window.TextEncoder);

const enc = (s) => new TextEncoder().encode(s);
const dec = (b) => new TextDecoder().decode(b);
const rand = (n) => window.crypto.getRandomValues(new Uint8Array(n));

export function b64(bytes) {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode.apply(null, u.subarray(i, i + 0x8000));
  return btoa(s);
}
export function unb64(s) {
  const bin = atob(s);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return u;
}

// ---------------------------------------------------------------- IndexedDB (a key-value store)
function openDb() {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function kv(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const req = fn(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally { db.close(); }
}
const kvGet = (k) => kv("readonly", (st) => st.get(k));
const kvPut = (k, v) => kv("readwrite", (st) => st.put(v, k));
const kvDel = (k) => kv("readwrite", (st) => st.delete(k));
const kvRange = (prefix) => kv("readonly", (st) => st.getAll(IDBKeyRange.bound(prefix, prefix + "￿")));
const kvRangeKeys = (prefix) => kv("readonly", (st) => st.getAllKeys(IDBKeyRange.bound(prefix, prefix + "￿")));

// ---------------------------------------------------------------- identity
/** 16 hex characters of SHA-256 over the raw public key; the server computes the same. */
export async function fingerprint(pubkeyB64) {
  if (!pubkeyB64) return "";
  const h = await window.crypto.subtle.digest("SHA-256", unb64(pubkeyB64));
  return Array.from(new Uint8Array(h).slice(0, 8)).map((x) => x.toString(16).padStart(2, "0")).join("");
}

export async function createIdentity() {
  const kp = await window.crypto.subtle.generateKey(CURVE, true, ["deriveBits"]);
  const pkcs8 = await window.crypto.subtle.exportKey("pkcs8", kp.privateKey);
  const pubkey = b64(await window.crypto.subtle.exportKey("raw", kp.publicKey));
  // Kept non-extractable from here on; pkcs8 exists only to make the backup.
  const priv = await window.crypto.subtle.importKey("pkcs8", pkcs8, CURVE, false, ["deriveBits"]);
  return { pubkey, fp: await fingerprint(pubkey), priv, pkcs8 };
}
export const loadIdentity = (uid) => kvGet("id:" + uid).then((v) => v || null);
export const storeIdentity = (uid, id) => kvPut("id:" + uid, { pubkey: id.pubkey, fp: id.fp, priv: id.priv });
export async function clearIdentity(uid) {
  await kvDel("id:" + uid);
  for (const k of await kvRangeKeys("ck:" + uid + ":")) await kvDel(k);
}

async function passKey(passphrase, salt) {
  const base = await window.crypto.subtle.importKey("raw", enc(String(passphrase).normalize("NFKC")), "PBKDF2", false, ["deriveKey"]);
  return window.crypto.subtle.deriveKey({ name: "PBKDF2", salt, iterations: PBKDF2_ITER, hash: "SHA-256" }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}
/** "b1.<salt>.<iv>.<encrypted pkcs8>.<public key>" — the public key rides along in the clear and is bound to the ciphertext. */
export async function makeBackup(pkcs8, pubkey, passphrase) {
  const salt = rand(16), iv = rand(12);
  const k = await passKey(passphrase, salt);
  const ct = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: enc(pubkey) }, k, pkcs8);
  return ["b1", b64(salt), b64(iv), b64(ct), pubkey].join(".");
}
export async function openBackup(blob, passphrase) {
  const p = String(blob || "").split(".");
  if (p.length !== 5 || p[0] !== "b1") throw new Error("malformed backup");
  const k = await passKey(passphrase, unb64(p[1]));
  const pkcs8 = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(p[2]), additionalData: enc(p[4]) }, k, unb64(p[3]));
  const priv = await window.crypto.subtle.importKey("pkcs8", pkcs8, CURVE, false, ["deriveBits"]);
  return { pubkey: p[4], fp: await fingerprint(p[4]), priv };
}

// ---------------------------------------------------------------- channel keys
export const importChannelKey = (raw) => window.crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
export async function newChannelKey() {
  const raw = rand(32);
  return { raw, key: await importChannelKey(raw) };
}

async function agree(priv, pubRaw) {
  const pub = await window.crypto.subtle.importKey("raw", pubRaw, CURVE, false, []);
  const bits = await window.crypto.subtle.deriveBits({ name: "ECDH", public: pub }, priv, 256);
  const base = await window.crypto.subtle.importKey("raw", bits, "HKDF", false, ["deriveKey"]);
  return window.crypto.subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: enc(WRAP_INFO) }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}
/** Wrap a raw channel key for one public key: "w1.<ephemeral public key>.<iv>.<ciphertext>". */
export async function wrapFor(pubkeyB64, raw) {
  const eph = await window.crypto.subtle.generateKey(CURVE, false, ["deriveBits"]);
  const ephPub = b64(await window.crypto.subtle.exportKey("raw", eph.publicKey));
  const k = await agree(eph.privateKey, unb64(pubkeyB64));
  const iv = rand(12);
  const ct = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: enc(ephPub) }, k, raw);
  return ["w1", ephPub, b64(iv), b64(ct)].join(".");
}
export async function unwrapWith(priv, blob) {
  const p = String(blob || "").split(".");
  if (p.length !== 4 || p[0] !== "w1") throw new Error("malformed wrapped key");
  const k = await agree(priv, unb64(p[1]));
  return new Uint8Array(await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(p[2]), additionalData: enc(p[1]) }, k, unb64(p[3])));
}
export const saveChannelKey = (uid, channel, ver, raw) => kvPut("ck:" + uid + ":" + channel + ":" + ver, { channel, ver, raw });
export const loadChannelKeys = (uid) => kvRange("ck:" + uid + ":");

// ---------------------------------------------------------------- messages
export const isEncrypted = (body) => typeof body === "string" && body.slice(0, 3) === "e1.";
export function parseEnc(body) {
  if (!isEncrypted(body)) return null;
  const p = body.split(".");
  if (p.length !== 4) return null;
  const ver = parseInt(p[1], 10);
  return ver > 0 ? { ver, iv: p[2], ct: p[3] } : null;
}
export async function encryptText(key, ver, text) {
  const iv = rand(12);
  const ct = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: enc("e1." + ver) }, key, enc(text));
  return ["e1", String(ver), b64(iv), b64(ct)].join(".");
}
export async function decryptText(key, body) {
  const e = parseEnc(body);
  if (!e) throw new Error("not encrypted");
  return dec(await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(e.iv), additionalData: enc("e1." + e.ver) }, key, unb64(e.ct)));
}
